import { chartInk, chartPalette, typography } from '../../design-system/tokens';

/**
 * The look every Plotly chart in Simple Coffee wears.
 *
 * Charts do not invent colours (Rule 17): series colours come from the
 * validated categorical palette in fixed order, and every piece of text wears a
 * text token rather than the series colour. Grid and axes stay recessive so the
 * data is the loudest thing on the page.
 */
export const PLOT_FONT = {
  family: typography.fontFamily,
  size: 13,
  color: chartInk.label,
};

/** Slot `index` of the palette. Assigned in sequence, never cycled per chart. */
export function seriesColor(index: number): string {
  return chartPalette[index % chartPalette.length] ?? chartPalette[0];
}

export interface AxisOptions {
  title?: string;
  /** Category axes get no gridlines: a line per bar is noise. */
  showGrid?: boolean;
  type?: 'category' | 'date' | 'linear';
}

export function axis({ title, showGrid = true, type }: AxisOptions = {}) {
  return {
    title: title ? { text: title, font: { ...PLOT_FONT, size: 12 } } : undefined,
    type,
    automargin: true,
    showgrid: showGrid,
    gridcolor: chartInk.grid,
    gridwidth: 1,
    zeroline: false,
    linecolor: chartInk.axis,
    tickfont: PLOT_FONT,
  };
}

/** The layout shared by every chart; callers override only what differs. */
export function baseLayout(options: { showLegend: boolean; height?: number }) {
  return {
    autosize: true,
    height: options.height ?? 260,
    margin: { l: 8, r: 8, t: 8, b: 8 },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: PLOT_FONT,
    showlegend: options.showLegend,
    legend: {
      orientation: 'h' as const,
      y: -0.25,
      font: PLOT_FONT,
    },
    hoverlabel: {
      bgcolor: chartInk.surface,
      bordercolor: chartInk.axis,
      font: { ...PLOT_FONT, color: chartInk.label },
    },
    colorway: [...chartPalette],
  };
}

/** No modebar, no logo, no export-to-Plotly: the page is the product. */
export const PLOT_CONFIG = {
  displayModeBar: false,
  responsive: true,
  staticPlot: false,
} as const;
